import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import type {TypedImageData} from "@cm/utils"
import {hashObject} from "@cm/utils"
import {ImageColorSpace} from "@cm/utils/data-object"
import {ImageNode} from "#template/nodes/image/image-node"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {ImageGenerator, ImageNodeGenerator, ImageNodeGeneratorData} from "@cm/material-nodes"
import {materialConnection, TexImage} from "@cm/material-nodes"

const colorSpaceName = (colorSpace?: ImageColorSpace | null): "Linear" | "sRGB" => (colorSpace === ImageColorSpace.Linear ? "Linear" : "sRGB")

import {TypeDescriptors as TD} from "#template/runtime-graph/type-descriptors"

const inMemoryImageDataDesc = TD.Identity<InMemoryImageParameters["imageData"]>()

export class InMemoryImageData {
    constructor(public readonly typedImageData: TypedImageData) {}
}

export type InMemoryImageParameters = {
    imageData: InMemoryImageData
}

const inMemoryImageDefinition: TemplateNodeDefinition<InMemoryImageParameters> = {
    nodeClass: "InMemoryImage",
    validation: {
        parameters: z.object({
            imageData: z.instanceof(InMemoryImageData),
        }),
        returns: getTemplateNodeReturns(),
    },
}

export function generateImageNodeForInMemoryImageData(
    typedImageData: TypedImageData,
    generatorData: ImageNodeGeneratorData,
): ReturnType<ImageNodeGenerator["generator"]> {
    const colorspace = typedImageData.colorSpace === "linear" ? ImageColorSpace.Linear : ImageColorSpace.Srgb

    const {uv, extension, interpolation, projection, disableProgressiveLoading, forceOriginalResolution} = generatorData

    const imgNode = new TexImage({
        parameters: {
            inputs: {
                vector: uv,
            },
            settings: {
                extension,
                interpolation,
                projection,
                disableProgressiveLoading,
                forceOriginalResolution,
                colorspace: colorspace,
                imageColorspaceSettingsName: colorSpaceName(colorspace),
                inMemoryImageData: typedImageData,
            },
        },
    })

    return {color: materialConnection(imgNode, "color"), alpha: materialConnection(imgNode, "alpha")}
}

@registerTemplateGraphNode
export class InMemoryImage extends ImageNode<InMemoryImageParameters> {
    public override get definition() {
        return inMemoryImageDefinition
    }

    public override evaluate(scope: GraphBuilderScope, _evaluator: NodeEvaluator): BuilderInlet<ImageGenerator | null> {
        return scope.pureLambda<InMemoryImageParameters["imageData"], ImageGenerator>(
            scope.value(this.parameters.imageData, inMemoryImageDataDesc, "imageData"),
            (imageData) => ({
                imageNode: {
                    generator: (imageGeneratorData: ImageNodeGeneratorData) =>
                        generateImageNodeForInMemoryImageData(imageData.typedImageData, imageGeneratorData),
                    hash: hashObject({
                        type: "InMemoryImage",
                        ...imageData,
                    }),
                },
            }),
            "result",
        )
    }
}
